/**
 * Win-condition checks. All four cases from `docs/rules.md` "Win conditions"
 * and the `Mr. X wins if every detective is stranded` clause.
 *
 * `checkWin` is pure: given the current public state and Mr. X's true station,
 * return either a `winner` value or `null`.
 *
 * Note: capture detection ("detective on Mr. X's station") needs Mr. X's
 * private station, so the caller must supply it. This is the engine's
 * standard private-state contract.
 */

import type { GameState } from '../types'
import type { Adjacency } from './board'
import { defaultAdjacency } from './board'
import { TOTAL_ROUNDS } from './constants'
import { hasAnyMrxMove } from './moves-internal'

export interface CheckWinArgs {
  state: GameState
  /** Mr. X's true current station. */
  mrxStation: number
  /** Adjacency map. Defaults to the canonical board. */
  adjacency?: Adjacency
  /**
   * If true, treat the round as having just *finished* (i.e. Mr. X has just
   * completed his Nth move). Used for the 24-round survival check.
   */
  mrxMoveJustCompleted?: boolean
}

/**
 * Returns the winner if a terminal condition is met, else `null`.
 *
 * Order of checks:
 *  1. Capture: any non-stranded detective shares Mr. X's station.
 *  2. All detectives stranded -> Mr. X wins.
 *  3. Mr. X just completed his 24th move and survived -> Mr. X wins.
 *  4. Mr. X has no legal move at the start of his turn -> detectives win.
 */
export function checkWin(args: CheckWinArgs): GameState['winner'] {
  const { state, mrxStation, adjacency = defaultAdjacency(), mrxMoveJustCompleted = false } = args

  // 1. Capture.
  for (const det of state.detectives) {
    if (!det.stranded && det.station === mrxStation) {
      return { team: 'detectives', reason: 'capture' }
    }
  }

  // 2. All detectives stranded.
  if (state.detectives.every((d) => d.stranded)) {
    return { team: 'mrx', reason: 'all_detectives_stranded' }
  }

  // 3. Mr. X survived 24 rounds.
  if (mrxMoveJustCompleted && state.round >= TOTAL_ROUNDS) {
    return { team: 'mrx', reason: 'survived_24_rounds' }
  }

  // 4. Mr. X cornered. Only relevant when it's Mr. X's turn to move.
  if (state.phase === 'mrx' && !mrxMoveJustCompleted) {
    if (!hasAnyMrxMove(state, mrxStation, adjacency)) {
      return { team: 'detectives', reason: 'mrx_cornered' }
    }
  }

  return null
}
